import CommonStockTable from "src/common/tables/CommonStockTable";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { ChevronLeft, ChevronRight, Download, Filter } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  formatWithOrdinal,
  getLocalStorageUser,
  parseMarketCap,
  toTitleCase,
} from "src/helpers/api/sharedFunction";

import axios from "axios";
import moment from "moment-timezone";
import InfoIcon from "src/assets/images/info.svg";
import InvestmentIcon from "src/assets/images/investment-analysis-icon.svg";
import ConfirmationModal from "src/common/ConfirmationModal";
import FreeTrialRestrictionModal, {
  RestrictionVariant,
} from "src/common/freeTrialRestrictionModal";
import PlanNotAvailable from "src/common/planNotAvailable";
import { Company, SearchContext } from "src/common/search";
import AppContext from "src/components/AppContext";
import CompanyInsightTabsModal from "src/components/dashboard/CompanyInsightTabsModal";
import CustomUniverseManager from "src/components/dashboard/CustomUniverseManager";
import DateTimePicker from "src/components/dashboard/DateTimePicker";
import ForensicInvestigationModal from "src/components/dashboard/ForensicInvestigationModal";
import WhatCompanyDoesModal from "src/components/dashboard/WhatCompanyDoesModal";
import { useSocket } from "src/components/SocketContext";
import {
  DataPoint,
  fetchWrapper,
  LtpMessage,
  renderCandleDetailsHtml,
  StockTableData,
  Watchlist,
} from "src/helpers";
import {
  getSortIcon,
  sortArray,
  SortConfig,
  SortDirection,
} from "src/helpers/api/sortconfig";
import { Stock, Universe } from "src/hooks/useCustomUniverses";
import { alertService } from "src/services";
import Charts from "./Charts";
import Filters, { FiltersFieldProps } from "./Filters";
import InsiderTradingModal from "./InsiderTradingModal";
import ListModal from "./listModal";
import {
  getRiskProfileUserKey,
  RISK_PROFILE_JUST_SELECTED_KEY,
} from "./RiskAppetiteModal";

import { FiltersData } from "pages/dashboard";
import CandleChart from "src/common/charts/candleChart";
import DialogModal from "src/common/DialogModal";
import { getFormattedDate } from "src/helpers/dateFormatter";
import { useStockCharts } from "src/hooks/useStockCharts";
import ChartModal from "../patternInsight/chartModal";

interface ListProps {
  getTabData: (date: string) => Promise<void>;
  selectedCompany: Company;
  setActiveTab: (tab: string) => void;
  activeTab: string;
  records: StockTableData[];
  setRecords: React.Dispatch<React.SetStateAction<StockTableData[]>>;
  setSelectedCompany: React.Dispatch<React.SetStateAction<Company>>;
  applyFilters: (date: string) => void;
  resetAllFilters: (date: string) => void;
  selectedFilters: FiltersFieldProps;
  setSelectedFilters: React.Dispatch<React.SetStateAction<FiltersFieldProps>>;
  toggleFilterModal: () => void;
  isFilterModalOpen: boolean;
  filtersData: FiltersData;
  setFiltersData: React.Dispatch<React.SetStateAction<FiltersData>>;
  queryString: string;
  appliedFilters: FiltersFieldProps;
  summary: Record<string, number>;
  totalSummary?: Record<string, number>;
  highlightedDates?: string[];
  universes: Universe[];
  setUniverses: React.Dispatch<React.SetStateAction<Universe[]>>;
  universeStocks: Record<string, Stock[]>;
  setUniverseStocks: React.Dispatch<
    React.SetStateAction<Record<string, Stock[]>>
  >;
  fetchUniverseStocks: (universeId: string, force?: boolean) => Promise<void>;
  isFetchingStocks: boolean;
}

export const defaultFilters: FiltersFieldProps = {
  sector: [],
  segment: [],
  near200SMA: [],
  fibLevel: [],
  stockTrend: [],
  machineLogic: "",
  propLogic: "",
  risk_profile: [],
};

const List = (props: ListProps) => {
  const {
    getTabData,
    selectedCompany,
    setActiveTab,
    activeTab,
    records,
    setSelectedCompany,
    applyFilters,
    resetAllFilters,
    selectedFilters,
    setSelectedFilters,
    toggleFilterModal,
    filtersData,
    isFilterModalOpen,
    setRecords,
    queryString,
    appliedFilters,
    summary,
    totalSummary,
    highlightedDates,
    universes,
    setUniverses,
    universeStocks,
    setUniverseStocks,
    fetchUniverseStocks,
    isFetchingStocks,
  } = props;
  const user = getLocalStorageUser();
  const searchContext = useContext(SearchContext);
  const { isLoading, setIsLoading, selectedDate, setSelectedDate } =
    searchContext;
  const token = user.token;

  const formattedDate: string | undefined = selectedDate
    ? moment(selectedDate).utcOffset("+05:30").format("YYYY-MM-DD")
    : undefined;

  const [activeRecordsDate, setActiveRecordsDate] = useState<
    string | undefined
  >(formattedDate);
  const [loadedViewKey, setLoadedViewKey] = useState("");
  const fetchIdRef = useRef(0);

  const hasCustomUniverseAccess =
    user?.plan === process.env.NEXT_PUBLIC_REQUIRED_PLAN;
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);
  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [isUniverseModalOpen, setIsUniverseModalOpen] = useState(false);
  const [isPlanNotAvailableOpen, setIsPlanNotAvailableOpen] = useState(false);
  const [planNotAvailableMode, setPlanNotAvailableMode] = useState<
    "customUniverse" | "restrictedTab"
  >("customUniverse");
  const [selectedStock, setSelectedStock] = useState<StockTableData | null>(
    null,
  );
  const [lineChartData, setLineChartData] = useState<DataPoint[]>([]);
  const [companyInfo, setCompanyInfo] = useState<Record<string, string>>();
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const userScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<any>(null);
  const context = useContext(AppContext);
  const { darkMode } = context.state;
  const isDark = !!darkMode;
  const [quantity, setQuantity] = useState<number>(1);
  const [buyPrice, setBuyPrice] = useState<number>(0);
  const [notes, setNotes] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [watchlist, setWatchlist] = useState<Watchlist>({});
  const [stockName, setStockName] = useState<any>();
  const [insiderTradingStockSymbol, setinsiderTradingStockSymbol] =
    useState("");
  const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);
  const [insightInitialTab, setInsightInitialTab] = useState("overview");
  const [selectedInsightCompany, setSelectedInsightCompany] = useState("");
  const [isBusinessModalOpen, setIsBusinessModalOpen] = useState(false);
  const [selectedBusinessCompany, setSelectedBusinessCompany] = useState("");
  const [isForensicModalOpen, setIsForensicModalOpen] = useState(false);
  const [selectedForensicCompany, setSelectedForensicCompany] = useState("");
  const [companyInfoLoading, setCompanyInfoLoading] = useState(false);
  const [chartStock, setChartStock] = useState<string | null>(null);
  const [freeTrialRestrictModal, setFreeTrialRestrictModal] = useState(false);
  const [freeTrialModalVariant, setFreeTrialModalVariant] =
    useState<RestrictionVariant>("dashboard");
  const [searchTrigger, setSearchTrigger] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const universeHasMutatedRef = useRef<boolean>(false);

  const [highlightSymbol, setHighlightSymbol] = useState<string>("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showArrows, setShowArrows] = React.useState({
    left: false,
    right: false,
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "",
    direction: null,
  });
  const [confirmationModal, setConfirmationModal] = useState(false);
  const currentDate = new Date();
  const selectedDateObj = selectedDate ? new Date(selectedDate) : null;
  const nextWorkingDay = user?.next_working_day
    ? new Date(user.next_working_day)
    : null;

  const {
    chartData,
    loading: loadingCharts,
    hoveredCandle,
    setHoveredCandle,
  } = useStockCharts(
    stockName?.symbol,
    token,
    false,
    false,
    false,
    false,
    undefined,
    false,
    false,
    false,
  );

  const recordsWithPercent = useMemo(
    () =>
      records.map((rec) => ({
        ...rec,
        current_percent:
          rec.ltp && rec.close ? ((rec.ltp - rec.close) / rec.close) * 100 : 0,
        market_cap_num: parseMarketCap(rec.market_cap),
        high_return_perc_num: Number(rec.high_return_perc) || 0,
      })),
    [records],
  );

  const handleOpenInsights = async (
    symbol: string,
    initialTab: string = "overview",
  ) => {
    setSelectedInsightCompany(symbol);
    setInsightInitialTab(initialTab);
    setIsInsightsModalOpen(true);
    setCompanyInfoLoading(true);
    try {
      const response = await fetchWrapper.get(
        "/api/dashboard/company_details?name=" + encodeURIComponent(symbol),
        undefined,
        {
          Authorization: `Bearer ${token}`,
        },
      );
      setCompanyInfo(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setCompanyInfoLoading(false);
    }
  };

  const handleOpenBusinessInsight = (symbol: string) => {
    setSelectedBusinessCompany(symbol);
    setIsBusinessModalOpen(true);
  };

  const handleOpenForensicInvestigation = (symbol: string) => {
    setSelectedForensicCompany(symbol);
    setIsForensicModalOpen(true);
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        let newDirection: SortDirection = null;
        if (prev.direction === null) newDirection = "desc";
        else if (prev.direction === "desc") newDirection = "asc";
        else if (prev.direction === "asc") newDirection = null;

        return { key, direction: newDirection };
      }

      return { key, direction: "desc" };
    });
  };

  const sortedRecords = useMemo(
    () => sortArray(recordsWithPercent, sortConfig),
    [recordsWithPercent, sortConfig],
  );

  const tabs: string[] = useMemo(() => {
    const base: string[] = [
      "Derivatives",
      "Nifty 500",
      "Nifty Micro 250",
      "Others",
      "Penny Stocks",
    ];
    // Add custom universe tabs dynamically if user has access (Nifty 750 plan)
    const custom = hasCustomUniverseAccess
      ? universes.map((u) => u.name).sort((a, b) => a.localeCompare(b))
      : [];

    return [...base, ...custom];
  }, [hasCustomUniverseAccess, universes]);

  const getTabCount = useCallback(
    (tab: string) => {
      return summary?.[tab] || 0;
    },
    [summary],
  );

  const restrictedTabs = useMemo(
    () => ["Nifty Micro 250", "Others", "Penny Stocks"],
    [],
  );
  const isRestrictedTab = useCallback(
    (tab: string) => user?.plan === "Nifty 500" && restrictedTabs.includes(tab),
    [user?.plan, restrictedTabs],
  );

  const firstTabWithData = tabs.find(
    (tab) => getTabCount(tab) > 0 && !isRestrictedTab(tab),
  );
  const willAutoSwitchTab =
    !!summary &&
    Object.keys(summary).length > 0 &&
    (getTabCount(activeTab) === 0 || isRestrictedTab(activeTab)) &&
    !!firstTabWithData &&
    firstTabWithData !== activeTab;

  useEffect(() => {
    if (!willAutoSwitchTab || !firstTabWithData) return;
    setActiveTab(firstTabWithData);
  }, [willAutoSwitchTab, firstTabWithData, setActiveTab]);

  const toggleInsiderTradingModal = (symbol: string) => {
    setinsiderTradingStockSymbol(symbol);
  };

  const openWatchlistModal = (stock: StockTableData) => {
    setSelectedStock(stock);

    if (!stock?.added_to_watchlist) {
      setIsOpen(true);
    } else {
      setConfirmationModal(true);
    }
    setWatchlist({
      symbol: stock.symbol,
      is_recurring: "",
    });
  };

  const openModal = (stock: StockTableData) => {
    setSelectedStock(stock);
    setQuantity(1);
    setBuyPrice(Number(stock.close));
    setNotes("");
    setIsPortfolioModalOpen(true);
  };

  useEffect(() => {
    const storedDate = sessionStorage.getItem("selectedDate");
    if (storedDate) {
      setSelectedDate(new Date(storedDate + "T00:00:00"));
      return;
    }
    // If trial and free_user_source_date; otherwise prefer last_source_date.
    if (user.free_user_source_date) {
      setSelectedDate(user.free_user_source_date);
      return;
    } else if (user.last_source_date) {
      setSelectedDate(user.last_source_date);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.free_user_source_date, user.last_source_date]);

  const appliedRiskProfileKey = (appliedFilters.risk_profile || []).join(",");
  const currentViewKey = `${activeTab}|${formattedDate ?? ""}|${appliedRiskProfileKey}`;

  useEffect(() => {
    if (!formattedDate) return;
    if (!user?.token) return;

    const viewKey = currentViewKey;
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);

    getTabData(formattedDate).finally(() => {
      if (fetchId !== fetchIdRef.current) return;
      setActiveRecordsDate(formattedDate);
      setLoadedViewKey(viewKey);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, formattedDate, appliedRiskProfileKey]);

  const lastSymbol = useRef<string>("");

  useEffect(() => {
    if (!selectedCompany.symbol || user?.is_subscribed === false) return;

    const getData = async (date: string) => {
      const isCustomUniverseTab = universes.some((u) => u.name === activeTab);
      let queryStr = `symbol=${encodeURIComponent(selectedCompany.symbol)}&date=${date}`;
      if (isCustomUniverseTab) {
        queryStr += `&universe_name=${encodeURIComponent(activeTab)}`;
      }

      const response = await fetchWrapper.get(
        `/api/dashboard?${queryStr}`,
        undefined,
        {
          Authorization: `Bearer ${token}`,
        },
      );

      if (Array.isArray(response.data) && response.data.length > 0) {
        const newTab = isCustomUniverseTab
          ? activeTab
          : response.data[0].indicesname;
        if (lastSymbol.current !== selectedCompany.symbol) {
          setActiveTab(newTab);
          lastSymbol.current = selectedCompany.symbol;
        }
      }
    };

    if (token) {
      const storedDate = sessionStorage.getItem("selectedDate");
      const dateToUse = selectedDate
        ? moment(selectedDate).tz("Asia/Kolkata").format("YYYY-MM-DD")
        : storedDate
          ? moment(storedDate).tz("Asia/Kolkata").format("YYYY-MM-DD")
          : user.last_source_date;

      getData(dateToUse);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany.symbol, selectedDate, token]);

  const handleRowClick = (stock: StockTableData) => {
    setSelectedCompany({ symbol: stock.symbol, company_name: "" });
  };

  const { emit, on, off, isConnected } = useSocket();

  const symbolsKey = useMemo(
    () =>
      records
        .map((r) => r.symbol)
        .sort()
        .join(","),
    [records],
  );

  useEffect(() => {
    if (!symbolsKey || !isConnected) return;

    const symbols = symbolsKey.split(",");

    on("stock_update", handleStockUpdate);
    emit("subscribe_stock", { symbol: symbols });

    return () => {
      off("stock_update", handleStockUpdate);
      emit("unsubscribe_stock", { symbol: symbols });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolsKey, isConnected]);

  const toISTMidnight = (date: string | number | Date) => {
    return moment(date).tz("Asia/Kolkata").startOf("day").valueOf();
  };

  const handleStockUpdate = (message: LtpMessage[]) => {
    const currentMidnightIST = toISTMidnight(currentDate);
    const nextWorkingMidnightIST = nextWorkingDay
      ? toISTMidnight(nextWorkingDay)
      : null;

    setRecords((prevRecords) => {
      let hasChanges = false;

      const updatedRecords = prevRecords.map((stock) => {
        const updated = message.find((m) => m.symbol_name === stock.symbol);

        // ----------  HANDLE NEXT-WORKING-DAY LOGIC ----------
        let diffInDays = 0;

        if (
          nextWorkingMidnightIST &&
          nextWorkingMidnightIST > currentMidnightIST
        ) {
          diffInDays =
            (nextWorkingMidnightIST - currentMidnightIST) /
            (1000 * 60 * 60 * 24);

          // Past → swap
          if (diffInDays < 0) {
            diffInDays =
              (currentMidnightIST - nextWorkingMidnightIST) /
              (1000 * 60 * 60 * 24);
          }
        }

        // If next working day is in future → do not update anything
        if (diffInDays > 1) return stock;

        // If no websocket update for this stock → skip
        if (!updated) return stock;

        // ---------- HANDLE LTP DATE (API DATE) ----------
        const ltpMidnightIST = toISTMidnight(updated.ltp_date);

        // If updated LTP date is >= 1 day old → ignore
        const ageInDays =
          (currentMidnightIST - ltpMidnightIST) / (1000 * 60 * 60 * 24);

        if (ageInDays >= 1) return stock;

        // ---------- PROCESS PRICE UPDATE ----------
        const newLtp = parseFloat(updated.ltp);
        const todaysHigh = parseFloat(updated.high);

        if (newLtp === 0) return stock; // invalid value

        let isModified = false;
        let nextStock = { ...stock };

        // If LTP changed
        if (newLtp !== stock.ltp) {
          nextStock.ltp = newLtp;
          nextStock.lastUpdated = new Date(Date.now() + 3000);
          isModified = true;
        }

        // ---------- SPECIAL CASE: IF STOCK DATE = TODAY (in IST) ----------
        const stockDateMidnightIST = toISTMidnight(stock.date);
        const todayIST = toISTMidnight(Date.now());

        if (stockDateMidnightIST === todayIST) {
          if (nextStock.todays_high !== 0) {
            nextStock.todays_high = 0;
            isModified = true;
          }
        } else {
          // ---------- HIGH-RETURN CALCULATIONS ----------
          // Only calculate if the high price strictly increased or wasn't set yet
          if (
            !nextStock.highest_price_afterdate ||
            nextStock.highest_price_afterdate < todaysHigh
          ) {
            const high_return_perc = (
              ((todaysHigh - nextStock.close) / nextStock.close) *
              100
            ).toFixed(2);

            nextStock.todays_high = todaysHigh;
            nextStock.highest_price_afterdate = todaysHigh;
            nextStock.highest_price_afterdate_date = moment(Date.now())
              .tz("Asia/Kolkata")
              .format("YYYY-MM-DD");
            nextStock.high_return_perc = high_return_perc;
            isModified = true;
          }
        }

        // Only return a new object reference if there were actual changes for this stock
        if (isModified) {
          hasChanges = true;
          return nextStock;
        }

        // If no changes, return the identical object to preserve identity
        return stock;
      });

      // ONLY return a new array if at least one stock actually changed its values above
      return hasChanges ? updatedRecords : prevRecords;
    });
  };

  const updatedStock = (stockSymbol: string, isWatchlist?: boolean) => {
    if (!stockSymbol) return;

    setRecords((prevRecords) =>
      prevRecords.map((item) =>
        item.symbol === stockSymbol
          ? {
              ...item,
              [isWatchlist ? "added_to_watchlist" : "added_to_portfolio"]: true,
            }
          : item,
      ),
    );
  };

  const exportStocksToCSV = async () => {
    try {
      setIsExporting(true);
      const user = JSON.parse(localStorage.getItem("BetaUser") || "{}");
      if (!user?.token) {
        console.error("No user token found in localStorage");
        return;
      }

      const response = await axios.get(`/api/dashboard/export?${queryString}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        responseType: "blob",
      });
      if (
        response.data.type === "application/json" ||
        response.headers["content-type"]?.includes("application/json")
      ) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        alertService.error(json.message || "Failed to export data");
        return;
      }

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let filename = "stocks-export.xlsx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting stocks:", error);
    } finally {
      setIsExporting(false);
    }
  };
  const handleResetField = () => {
    setWatchlist?.({ symbol: "", is_recurring: "" });
    setNotes("");
    setQuantity?.(0);
    setBuyPrice?.(0);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      userScrollingRef.current = true;
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        userScrollingRef.current = false;
      }, 2000);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const recordSymbolsKey = useMemo(
    () => records.map((r) => r.symbol).join(","),
    [records],
  );

  useEffect(() => {
    if (selectedCompany.symbol) {
      setSearchTrigger(Date.now());
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (!selectedCompany?.symbol) return;

    const normalized = selectedCompany.symbol.replace(".NS", "");
    setHighlightSymbol("");

    const timer = setTimeout(() => {
      setHighlightSymbol(normalized);
      userScrollingRef.current = false;
    }, 150);

    return () => clearTimeout(timer);
  }, [selectedCompany.symbol, activeTab, recordSymbolsKey, searchTrigger]);

  let heading: React.ReactNode = "";

  if (selectedDateObj) {
    const currentMidnightIST = moment
      .tz(currentDate, "Asia/Kolkata")
      .startOf("day");
    const selectedMidnightIST = moment
      .tz(selectedDateObj, "Asia/Kolkata")
      .startOf("day");

    const diffInDays = selectedMidnightIST.diff(currentMidnightIST, "days");

    if (diffInDays === 0) {
      heading = (
        <span>
          to focus{" "}
          <span className="px-1 py-3 text-[#247054ff] dark:text-[#14D83E]">
            Today
          </span>
        </span>
      );
    } else if (diffInDays === 1) {
      heading = (
        <span>
          to focus{" "}
          <span className="px-1 py-3 text-[#247054ff] dark:text-[#14D83E]">
            Tomorrow
          </span>
        </span>
      );
    } else {
      const formattedDate = formatWithOrdinal(selectedMidnightIST.toDate());
      heading = (
        <span>
          to focus on{" "}
          <span
            className="px-1 py-3 text-[#247054ff] dark:text-[#14D83E]"
            dangerouslySetInnerHTML={{ __html: formattedDate }}
          />
        </span>
      );
    }
  }

  const handleOnClick = async (response: string) => {
    if (response === "Delete") {
      if (!token) {
        alertService.error("Authentication token not found");
        return;
      }
      try {
        await fetchWrapper.delete(
          `/api/users/watchlists/delete?stock_name=${selectedStock?.symbol}`,
          undefined,
          { Authorization: `Bearer ${token}` },
        );

        const storedUser = getLocalStorageUser();

        if (storedUser) {
          const updatedUser = {
            ...storedUser,
            watchlist_count: Math.max(
              Number(storedUser.watchlist_count ?? 0) - 1,
              0,
            ),
          };

          localStorage.setItem("BetaUser", JSON.stringify(updatedUser));
        }

        setRecords((prevRecords) =>
          prevRecords.map((item) =>
            item.symbol === selectedStock?.symbol
              ? { ...item, added_to_watchlist: false }
              : item,
          ),
        );
        setConfirmationModal(false);
      } catch (error) {
        alertService.error("Failed to delete watchlist item");
        console.error("Delete error:", error);
      }
    } else {
      setConfirmationModal(false);
    }
  };
  const getAppliedFilterCount = (filters: any) => {
    let count = 0;

    Object.values(filters).forEach((value) => {
      if (Array.isArray(value)) {
        if (value.length > 0) count += 1;
      } else if (typeof value === "string" && value.trim() !== "") {
        count += 1;
      } else if (typeof value === "boolean" && value === true) {
        count += 1;
      }
    });

    return count;
  };
  const totalAppliedFilters = getAppliedFilterCount(appliedFilters);

  const isTableLoading =
    isLoading ||
    !formattedDate ||
    loadedViewKey !== currentViewKey ||
    willAutoSwitchTab;

  const riskProfileUserKey = getRiskProfileUserKey(user || null);
  const justSelectedRiskProfile =
    typeof window !== "undefined" &&
    !!riskProfileUserKey &&
    sessionStorage.getItem(RISK_PROFILE_JUST_SELECTED_KEY) ===
      riskProfileUserKey;

  useEffect(() => {
    if (isTableLoading || !justSelectedRiskProfile) return;
    if (records.length > 0) {
      sessionStorage.removeItem(RISK_PROFILE_JUST_SELECTED_KEY);
    }
  }, [isTableLoading, justSelectedRiskProfile, records.length]);

  const isFirstTimeRiskEmpty =
    !isTableLoading && justSelectedRiskProfile && records.length === 0;

  const isTotalSummaryEmpty =
    !isTableLoading &&
    (!totalSummary ||
      Object.keys(totalSummary).length === 0 ||
      Object.values(totalSummary).every((val) => val === 0));

  const shouldDisableFilters = isTotalSummaryEmpty;

  const portfolioCount = Number(user?.portfolio_count ?? 0);
  const watchlistCount = Number(user?.watchlist_count ?? 0);
  const stockLimit = Number(process.env.NEXT_PUBLIC_STOCK_COUNT_LIMIT);
  const isPortfolioLimitReached =
    user?.is_trial && portfolioCount >= stockLimit;
  const isWatchlistLimitReached =
    user?.is_trial && watchlistCount >= stockLimit;

  const get52WeeksChartData = useCallback(
    async (symbol: string, formattedDate: string | undefined) => {
      if (!symbol) return;

      setIsLoadingChart(true);
      try {
        const response = await fetchWrapper.get(
          `/api/dashboard/ohlcdata?name=${encodeURIComponent(symbol)}&forPeakReturn=true&date=${formattedDate}`,
          undefined,
          {
            Authorization: `Bearer ${token}`,
          },
        );
        setLineChartData(response?.data || []);
      } catch (_error) {
        setLineChartData([]);
      } finally {
        setIsLoadingChart(false);
      }
    },
    [token],
  );

  const handleChartClick = (symbol: string) => {
    setChartStock(symbol);
    setIsChartModalOpen(true);
    // Optionally: get52WeeksChartData(symbol); // Fetch immediately
  };

  const closeModal = () => {
    setIsChartModalOpen(false);
    setChartStock(null);
    setLineChartData([]); // Clear data when closing
  };

  const handleScrollIcons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      // We use a 5px threshold to account for sub-pixel rendering
      setShowArrows({
        left: scrollLeft > 5,
        right: scrollLeft < scrollWidth - clientWidth - 5,
      });
    }
  };

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      // Scroll by the full visible width to ensure tabs don't get cut in half
      const scrollAmount = direction === "left" ? -clientWidth : clientWidth;

      scrollRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  React.useEffect(() => {
    handleScrollIcons();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener("scroll", handleScrollIcons);
      window.addEventListener("resize", handleScrollIcons);
      return () => {
        el.removeEventListener("scroll", handleScrollIcons);
        window.removeEventListener("resize", handleScrollIcons);
      };
    }
  }, [tabs]);

  return (
    <>
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center mb-0">
        {/* Header + Search Row */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center w-full pt-1">
          {/* Title + Info */}
          <div className="flex w-full justify-center lg:justify-between items-center flex-wrap space-x-2 mb-3 lg:mb-0">
            <div className="flex items-center gap-2">
              <h2 className="text-xl pl-2 py-1 text-black dark:text-white font-figtree">
                List of high probability stocks {heading}
              </h2>

              <div className="relative group">
                <Image
                  src={InfoIcon}
                  alt="Info"
                  className="w-5 h-5 cursor-pointer"
                />
                <div className="absolute z-10 hidden group-hover:block w-64 bg-[#30324e] text-white text-sm p-3 rounded-md shadow-lg border border-[#394564] left-full ml-2">
                  Info about high probability stocks
                  {/* <div className="absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-r-4 border-r-[#30324e]"></div> */}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  if (hasCustomUniverseAccess) {
                    universeHasMutatedRef.current = false;
                    setIsUniverseModalOpen(true);
                  } else {
                    setPlanNotAvailableMode("customUniverse");
                    setIsPlanNotAvailableOpen(true);
                  }
                }}
                className="group flex items-center gap-2 text-sm font-medium transition-all duration-300 cursor-pointer"
              >
                {/* Glowing Icon */}
                <div className="relative flex items-center justify-center">
                  {/* Subtle Glow behind the icon */}
                  <div className="absolute inset-0 bg-indigo-500/20 blur-md rounded-full opacity-50 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* Gradient Text with Hover Underline */}
                <span className="bg-gradient-to-r from-indigo-600 to-cyan-500 dark:from-cyan-400 dark:to-indigo-400 bg-clip-text text-transparent tracking-wide font-semibold group-hover:brightness-125 transition-all duration-300 relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[1px] after:bg-gradient-to-r after:from-indigo-600 after:to-cyan-500 dark:after:from-cyan-400 dark:after:to-indigo-400 after:transition-all after:duration-300 group-hover:after:w-full">
                  Custom Universe
                </span>
              </button>
              <span className="text-gray-400">|</span>
              <Link
                href="/portfolio"
                className="text-sm font-medium text-black dark:text-white hover:text-blue-800 hover:dark:text-blue-300 transition-colors"
              >
                My Portfolio
              </Link>
              <span className="text-gray-400">|</span>
              <Link
                href="/watchlist"
                className="text-sm font-medium text-black dark:text-white hover:text-blue-800 hover:dark:text-blue-300 transition-colors"
              >
                My Watchlist
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-md border border-[#d0d4e4] dark:border-[#394564] bg-white dark:bg-[#121419] shadow-[5px_1px_5px_rgba(0,0,0,0.09)] mb-1">
        {/* Tabs */}
        <div className="flex flex-nowrap items-center border-b border-[#d0d4e4] dark:border-[#394564] w-full bg-white dark:bg-[#121419]">
          <div className="relative flex-1 flex items-center overflow-hidden group h-full">
            {showArrows.left && (
              <button
                onClick={() => scroll("left")}
                className="absolute left-0 z-30 h-full px-2 bg-gradient-to-r from-white via-white/90 to-transparent dark:from-[#121419] dark:via-[#121419]/90 dark:to-transparent cursor-pointer flex items-center"
              >
                <ChevronLeft className="w-5 h-5 text-gray-500 hover:text-black dark:hover:text-white" />
              </button>
            )}
            <div
              ref={scrollRef}
              className="flex items-center overflow-x-auto no-scrollbar scroll-smooth py-3 px-4 gap-1"
              style={{ scrollSnapType: "x mandatory" }}
            >
              {tabs.map((tab) => {
                const count = getTabCount(tab);
                const isRestricted = isRestrictedTab(tab);
                const isDisabled = count === 0;
                const isCustom = universes.some((u) => u.name === tab);

                return (
                  <div key={tab} className="relative shrink-0">
                    <button
                      onClick={(e) => {
                        if (isRestricted) {
                          e.preventDefault();
                          setPlanNotAvailableMode("restrictedTab");
                          setIsPlanNotAvailableOpen(true);
                          return;
                        }
                        if (!isDisabled) {
                          setActiveTab(tab);
                        }
                      }}
                      disabled={isDisabled}
                      className={`px-2 py-1.5 mr-1 rounded text-base font-medium transition-colors font-figtree relative ${
                        isDisabled
                          ? "cursor-not-allowed opacity-60 text-gray-400 dark:text-gray-300"
                          : "cursor-pointer"
                      } ${
                        !isDisabled && activeTab === tab
                          ? isCustom
                            ? "border-b-4 border-indigo-500 text-indigo-600 dark:text-cyan-400 bg-indigo-50/50 dark:bg-indigo-900/10"
                            : "border-b border-b-4 border-[#a2dca9] dark:border-[#ffb597] text-[#0a7848] dark:text-[#ffb597]"
                          : !isDisabled
                            ? isCustom
                              ? "text-indigo-400 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                              : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5"
                            : ""
                      }`}
                    >
                      <span className="flex items-center gap-1">
                        {isCustom ? toTitleCase(tab) : tab}
                      </span>

                      {count > 0 && (
                        <span
                          className={`absolute z-1 -top-1 -right-1 p-[1px] rounded shadow-sm ${
                            isCustom
                              ? "bg-gradient-to-r from-indigo-500 to-cyan-500"
                              : "bg-gradient-to-r from-teal-400 to-emerald-500"
                          }`}
                        >
                          <span className="flex items-center justify-center bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30 rounded-[3px] min-w-[16px] h-4 px-1 text-[10px] font-bold leading-tight text-gray-800 dark:text-gray-200">
                            {count}
                          </span>
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
            {showArrows.right && (
              <button
                onClick={() => scroll("right")}
                className="absolute right-0 z-30 h-full px-2 bg-gradient-to-l from-white via-white/90 to-transparent dark:from-[#121419] dark:via-[#121419]/90 dark:to-transparent cursor-pointer flex items-center"
              >
                <ChevronRight className="w-5 h-5 text-gray-500 hover:text-black dark:hover:text-white" />
              </button>
            )}
          </div>
          <div className="sm:gap-0 space-x-2 shrink-0 dark:border-[#394564] flex items-center px-4 h-full bg-white dark:bg-[#121419]">
            <div className="relative">
              <DateTimePicker
                isDashboard={true}
                lastDate={user?.last_source_date}
                freeUserSourceDate={user?.free_user_source_date}
                isTrial={user?.is_trial}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                onBlockedSelection={setFreeTrialRestrictModal}
                setFreeTrialModalVariant={setFreeTrialModalVariant}
                highlightedDates={highlightedDates}
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (!shouldDisableFilters) toggleFilterModal();
                }}
                disabled={shouldDisableFilters}
                className={`group relative flex items-center space-x-1 px-3 py-[5px] rounded-md transition-colors ${
                  shouldDisableFilters
                    ? "text-gray-400 bg-gray-200 dark:bg-[#1f2229] dark:text-gray-500 cursor-not-allowed opacity-70"
                    : "text-[#3D3D3D] bg-[#e9e9e9] dark:bg-transparent dark:text-white hover:bg-gray-800 cursor-pointer"
                }`}
              >
                {totalAppliedFilters > 0 && (
                  <span className="absolute -top-1 -right-2 p-[1px] rounded bg-gradient-to-r from-teal-400 to-emerald-500 shadow-sm">
                    <span className="flex items-center justify-center bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30 rounded-[3px] min-w-[16px] h-4 px-1 text-[10px] font-bold leading-tight text-gray-800 dark:text-gray-200">
                      {totalAppliedFilters}
                    </span>
                  </span>
                )}

                <Filter
                  className="w-4 h-4 text-[#3D3D3D] group-hover:text-white transition-colors 
      text-black font-medium dark:text-white"
                />

                <span
                  className="text-sm hidden min-[1539px]:inline max-[425px]:text-sm 
      text-[#3D3D3D] group-hover:text-white transition-colors text-black 
      font-medium dark:text-white"
                >
                  Advanced Filters
                </span>
              </button>

              <button
                onClick={exportStocksToCSV}
                disabled={
                  isExporting || (!isTableLoading && sortedRecords.length === 0)
                }
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm text-white transition-all duration-200 font-figtree 
    ${
      isExporting || (!isTableLoading && sortedRecords.length === 0)
        ? "bg-gray-400 cursor-not-allowed opacity-70"
        : "bg-[#00A85F] dark:bg-gradient-to-t dark:from-[#066766] dark:to-[#0FA243] hover:opacity-90 cursor-pointer"
    }`}
              >
                <Download className="w-4 h-4" />
                <span className="hidden min-[1539px]:inline">
                  {isExporting ? "Exporting..." : "Export Data"}
                </span>
              </button>

              <button
                onClick={() => setIsTableCollapsed(!isTableCollapsed)}
                className="flex items-center space-x-1 rounded-md text-sm text-white transition-all duration-200 font-figtree filter invert dark:invert-0 cursor-pointer "
              >
                <span>{isTableCollapsed ? "" : ""}</span>
                <svg
                  className={`w-4 h-4 transform transition-transform ${isTableCollapsed ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className={`transition-all duration-300 ${isTableCollapsed ? "max-h-0 overflow-hidden" : ""}`}
          ref={containerRef}
        >
          <CommonStockTable
            records={sortedRecords}
            isLoading={isTableLoading}
            containerClassName={`min-h-[325px] ${sortedRecords.length > 0 ? "max-h-[325px]" : ""}`}
            sortConfig={sortConfig}
            onSort={handleSort}
            getSortIcon={getSortIcon}
            onRowClick={handleRowClick}
            selectedCompanySymbol={selectedCompany.symbol}
            highlightSymbol={highlightSymbol}
            onHighlightEnd={(symbol) => {
              if (highlightSymbol === symbol) setHighlightSymbol("");
            }}
            darkMode={darkMode}
            isDashboard={true}
            selectedDate={formattedDate ?? ""}
            onAddPortfolio={(record) => openModal(record)}
            onAddWatchlist={(record) => openWatchlistModal(record)}
            onOpenInsights={(symbol, tab) => handleOpenInsights(symbol, tab)}
            onOpenBusinessInsight={(symbol) =>
              handleOpenBusinessInsight(symbol)
            }
            onOpenForensicInsight={(symbol) =>
              handleOpenForensicInvestigation(symbol)
            }
            onOpenInsiderTrading={(symbol) => toggleInsiderTradingModal(symbol)}
            onOpenChart={(symbol, date) => {
              handleChartClick(symbol);
              get52WeeksChartData(symbol, date);
            }}
            onOpenOrderBlock={(record) => {
              setStockName(record);
              setShowModal(true);
            }}
            isPortfolioLimitReached={(record) => {
              return !!(isPortfolioLimitReached && !record?.added_to_portfolio);
            }}
            isWatchlistLimitReached={() => {
              return !!isWatchlistLimitReached;
            }}
            onLimitReached={() => {
              setFreeTrialModalVariant("addStock");
              setFreeTrialRestrictModal(true);
            }}
            emptyState={
              <div className="py-6 px-6 text-center text-xl md:text-2xl font-normal text-gray-700 dark:text-[#ffffff] tracking-wide font-figtree">
                {isTotalSummaryEmpty ? (
                  <>
                    <span className="text-center text-gray-700 dark:text-[#ffffff] text-xl md:text-2xl font-normal tracking-wide font-figtree">
                      Time for a break! No high probability stocks found across
                      all indices.
                    </span>
                    <DotLottieReact
                      src="/lottie/chillswing.json"
                      loop
                      autoplay
                      className="w-full max-w-[300px] mx-auto mt-4"
                    />
                  </>
                ) : isFirstTimeRiskEmpty ? (
                  <span>
                    No stocks found, click on{" "}
                    <button
                      type="button"
                      onClick={toggleFilterModal}
                      className="underline font-medium text-[#00A85F] hover:opacity-80 cursor-pointer"
                    >
                      advanced filter
                    </button>{" "}
                    to change your Risk Profile.
                  </span>
                ) : (
                  <span>
                    No stocks found for your selected filters, try reducing
                    filters via{" "}
                    <button
                      type="button"
                      onClick={toggleFilterModal}
                      className="underline font-medium text-[#00A85F] hover:opacity-80 cursor-pointer"
                    >
                      advanced filter
                    </button>
                    .
                  </span>
                )}
              </div>
            }
          />

          {isFilterModalOpen && (
            <Filters
              filtersData={filtersData}
              toggleFilterModal={toggleFilterModal}
              resetAllFilters={resetAllFilters}
              applyFilters={applyFilters}
              selectedDate={formattedDate ?? ""}
              setSelectedFilters={setSelectedFilters}
              selectedFilters={selectedFilters}
              appliedFilters={appliedFilters}
            />
          )}
          {insiderTradingStockSymbol !== "" && (
            <InsiderTradingModal
              toggleInsiderTradingModal={toggleInsiderTradingModal}
              symbol={insiderTradingStockSymbol}
              token={token}
            />
          )}
          {isPortfolioModalOpen && selectedStock && (
            <DialogModal
              title=""
              message={
                <ListModal
                  heading="Add to Portfolio"
                  investmentImg={InvestmentIcon}
                  investmentAltText={"Investment"}
                  notes={notes}
                  selectedStock={selectedStock}
                  quantity={quantity}
                  updatedStock={updatedStock}
                  setQuantity={setQuantity}
                  buyPrice={buyPrice}
                  setBuyPrice={setBuyPrice}
                  setNotes={setNotes}
                  isDashboard={true}
                  handleOnClick={() => {
                    setIsPortfolioModalOpen(false);
                    handleResetField();
                  }}
                  isModalOpen={isPortfolioModalOpen}
                />
              }
              isModalList={true}
              show={isPortfolioModalOpen}
              setShow={setIsPortfolioModalOpen}
              handleOnClick={() => {
                setIsPortfolioModalOpen(false);
                handleResetField();
              }}
              noBackdrop={true}
            />
          )}

          {isOpen && selectedStock && (
            <DialogModal
              title=""
              message={
                <ListModal
                  heading="Add to Watchlist"
                  isDashboard={true}
                  isWatchlist={true}
                  notes={notes}
                  updatedStock={updatedStock}
                  selectedStock={selectedStock}
                  setNotes={setNotes}
                  handleOnClick={() => {
                    setIsOpen(false);
                    handleResetField();
                  }}
                  watchlist={watchlist}
                  setWatchlist={setWatchlist}
                  getTabData={getTabData}
                  date={formattedDate ?? ""}
                />
              }
              isModalList={true}
              show={isOpen}
              setShow={setIsOpen}
              handleOnClick={() => {
                setIsOpen(false);
                handleResetField();
              }}
              noBackdrop={true}
            />
          )}

          {showModal && stockName && (
            <DialogModal
              title={`
                <div class="flex items-center gap-1.5 min-w-0 pr-2 xl:pr-6">
                  <span class="font-bold uppercase tracking-wide text-black dark:text-white whitespace-nowrap shrink-0">${stockName.symbol}: 1 Year Chart (TF: 1 Day)</span>
                  <div class="flex items-center gap-1.5 text-[12px] xl:text-[13px] font-medium tracking-tight whitespace-nowrap shrink-0">
                    <span class="text-gray-400 uppercase">Order Block On: <span class="dark:text-gray-200 text-black"> ${getFormattedDate(stockName.obdate)}</span></span>
                    <span class="text-gray-500 font-bold hidden sm:inline">|</span>
                    <span class="text-gray-400 uppercase">Retested on: <span class="dark:text-gray-200 text-black">${getFormattedDate(stockName.retest_date)}</span></span>
                    <span class="text-gray-500 font-bold hidden sm:inline">|</span>
                    <span class="text-gray-400 uppercase">Timespan: <span class="text-[#14D83E] font-bold">${stockName.retest_icondays} Days</span></span>
                  </div>
                  ${!chartData || chartData.length === 0 ? "" : renderCandleDetailsHtml(hoveredCandle || chartData[chartData.length - 1])}
                </div>
              `}
              message={
                <CandleChart
                  symbolName={stockName.symbol}
                  candleData={chartData}
                  darkMode={isDark}
                  isExpand={true}
                  isOrderblock={true}
                  obDate={stockName.obdate}
                  isOrderBlock={true}
                  retestDate={stockName.retest_date}
                  onHoverChange={setHoveredCandle}
                />
              }
              show={showModal}
              setShow={setShowModal}
              isChart={true}
              loadingCharts={loadingCharts}
            />
          )}

          {confirmationModal && (
            <ConfirmationModal
              title={"Remove Stock from WatchList"}
              message={"Are you sure you want to remove stock from WatchList?"}
              show={confirmationModal}
              setShow={setConfirmationModal}
              isDeleteModal={true}
              handleOnClick={handleOnClick}
            />
          )}
        </div>
      </div>
      {records.length > 0 && (
        <Charts
          symbols={records.map((r) => r.symbol).join(",")}
          formattedDate={activeRecordsDate}
          isDashboard={true}
        />
      )}

      {isInsightsModalOpen && (
        <CompanyInsightTabsModal
          show={isInsightsModalOpen}
          setShow={setIsInsightsModalOpen}
          selectedCompany={selectedInsightCompany}
          companyInfo={companyInfo}
          companyInfoLoading={companyInfoLoading}
          variant="dataDriven"
          initialTab={insightInitialTab}
        />
      )}

      {isBusinessModalOpen && (
        <WhatCompanyDoesModal
          show={isBusinessModalOpen}
          setShow={setIsBusinessModalOpen}
          selectedCompany={selectedBusinessCompany}
        />
      )}

      {isForensicModalOpen && (
        <ForensicInvestigationModal
          show={isForensicModalOpen}
          setShow={setIsForensicModalOpen}
          selectedCompany={selectedForensicCompany}
        />
      )}
      <FreeTrialRestrictionModal
        isOpen={freeTrialRestrictModal}
        onClose={() => setFreeTrialRestrictModal(false)}
        variant={freeTrialModalVariant}
      />

      <PlanNotAvailable
        isOpen={isPlanNotAvailableOpen}
        onClose={() => setIsPlanNotAvailableOpen(false)}
        requiredPlan={process.env.NEXT_PUBLIC_REQUIRED_PLAN}
        currentPlan={user?.plan}
        mode={planNotAvailableMode}
      />

      {isUniverseModalOpen && (
        <DialogModal
          title=""
          isUniverse={true}
          noBackdrop={true}
          message={
            <CustomUniverseManager
              universes={universes}
              setUniverses={setUniverses}
              universeStocks={universeStocks}
              setUniverseStocks={setUniverseStocks}
              fetchUniverseStocks={fetchUniverseStocks}
              isFetchingStocks={isFetchingStocks}
              onMutate={() => {
                universeHasMutatedRef.current = true;
              }}
            />
          }
          show={isUniverseModalOpen}
          setShow={(action) => {
            const newValue =
              typeof action === "function"
                ? action(isUniverseModalOpen)
                : action;
            setIsUniverseModalOpen(newValue);
            if (!newValue) {
              const hasChanged = universeHasMutatedRef.current;

              if (hasChanged && formattedDate) {
                getTabData(formattedDate).finally(() => {
                  setActiveRecordsDate(formattedDate);
                });
              }
            }
          }}
          maxWidth="max-w-7xl w-[90vw]"
          minHeight="xl:h-[75vh] h-[65vh]"
        />
      )}

      {chartStock && isChartModalOpen && (
        <ChartModal
          isOpen={isChartModalOpen}
          onClose={closeModal}
          stock={lineChartData}
          darkMode={darkMode}
          selectedStock={chartStock}
          selectedDate={
            new Date(
              moment(records[0].date).tz("Asia/Kolkata").format("YYYY-MM-DD"),
            )
          }
          isDashboardModal={true}
          isLoadingChart={isLoadingChart} // Add this prop if you want loading state
        />
      )}
    </>
  );
};

export default React.memo(List);
